'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { Logo } from './Logo';

interface LoadingScreenProps {
  /** Muted caption under the mark. Pass '' to hide. */
  label?: string;
  /** Fill the parent instead of a viewport-height block (e.g. inside a panel). */
  fill?: boolean;
  className?: string;
}

/**
 * Branded loading state — the CANNECT.AI wordmark rendered as a subtle 3D object
 * that gently rotates side-to-side on its vertical axis over a breathing brand
 * glow, with a slim indeterminate track underneath. Universal: use as a Suspense
 * fallback or any "loading…" placeholder. Honors `prefers-reduced-motion`.
 */
export function LoadingScreen({ label = 'Загрузка…', fill = false, className = '' }: LoadingScreenProps) {
  const reduce = useReducedMotion();

  return (
    <div
      className={[
        'relative flex w-full flex-col items-center justify-center gap-8 overflow-hidden bg-[#0e0a16]',
        fill ? 'h-full min-h-0' : 'h-full min-h-[60vh]',
        className,
      ].join(' ')}
    >
      {/* soft brand glow */}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute h-80 w-80 rounded-full bg-brand-600/20 blur-[100px]"
        animate={reduce ? undefined : { opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.08, 0.9] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* wordmark as a 3D object gently turning on its Y axis */}
      <div style={{ perspective: '900px' }} className="relative">
        <motion.div
          style={{ transformStyle: 'preserve-3d' }}
          className="drop-shadow-[0_12px_34px_rgba(91,53,255,0.4)]"
          animate={reduce ? undefined : { rotateY: [-18, 18, -18], y: [0, -3, 0] }}
          transition={{ duration: 3.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Logo className="h-9 w-auto text-white sm:h-10" />
        </motion.div>
      </div>

      {/* slim indeterminate track */}
      <div className="relative h-0.5 w-40 overflow-hidden rounded-full bg-white/[0.08]">
        <motion.span
          className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-brand-400 to-transparent"
          initial={{ x: '-110%' }}
          animate={reduce ? { x: '150%' } : { x: ['-110%', '360%'] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {label ? <p className="text-xs tracking-wide text-white/40">{label}</p> : null}
    </div>
  );
}
